/* ==========================================================================
   AUMI — global.js (sem dependências)

   CONTRATO PÚBLICO (window.AUMI, criado em layout/theme.liquid e completado aqui)
   ---------------------------------------------------------------------------
   AUMI.cart.add(formDataOuObjeto)   → Promise<item>  (POST /cart/add.js)
   AUMI.cart.change(key, quantity)   → Promise<cart>  (POST /cart/change.js)
   AUMI.cart.update(objeto)          → Promise<cart>  (POST /cart/update.js — ex.: { note })
   AUMI.cart.refresh()               → Promise<cart>  (GET /cart.js + re-render)
   AUMI.cart.sections                → array de section ids re-renderizados a cada mudança
                                        (padrão ['cart-drawer']; a página de carrinho pode dar push('main-cart'))
   AUMI.openDrawer('cart'|'menu'|'search'|<nome>)  ·  AUMI.closeDrawers()
   AUMI.formatMoney(cents)           → "R$ 1.234,56"
   AUMI.toast(msg, { duration })
   AUMI.reveal(root)                 → observa [data-reveal] novos dentro de root

   EVENTOS (em document)
   aumi:cart-updated   detail: { cart, source: 'add'|'change'|'update'|'refresh', item? }
   aumi:cart-error     detail: { message, source }
   aumi:drawer-open    detail: { name }     ·  aumi:drawer-close  detail: { name }

   RE-RENDER DO CARRINHO — abordagem escolhida: Section Rendering API "bundled".
   /cart/add.js e /cart/change.js recebem `sections` + `sections_url` e devolvem o HTML
   renderizado junto da resposta (uma única ida ao servidor). refresh() usa
   GET {root}?sections=cart-drawer. No HTML devolvido, todo elemento com
   [data-cart-render="<chave>"] substitui o innerHTML do elemento de mesma chave na página.
   Assim o <aside data-drawer="cart"> nunca é trocado (mantém .is-open e o foco).

   SELETORES DECLARATIVOS
   [data-open-drawer="cart|menu|search"]  abre (em <a>, só previne a navegação se o drawer existir)
   [data-close-drawer]                    fecha tudo
   [data-drawer="nome"]                   o painel (ganha .is-open; fechado recebe inert)
   form[data-product-form]                interceptado → cart.add, botão submit ganha .is-loading
   [data-cart-count]                      recebe a quantidade de itens (hidden quando 0)
   [data-cart-qty-change][data-key][data-quantity] · [data-cart-remove][data-key]
   input[data-cart-qty-input][data-key]  · textarea[data-cart-note]
   [data-reveal]                          ganha .is-revealed ao entrar na viewport
   <slider-nav target="#id">              setas para .slider (botões [data-slider-prev]/[data-slider-next])
   <predictive-search>                    input[type=search] + [data-predictive-results]
   ========================================================================== */
(() => {
  'use strict';

  const AUMI = (window.AUMI = window.AUMI || {});
  AUMI.routes = AUMI.routes || {};
  AUMI.settings = AUMI.settings || {};
  const root = (AUMI.routes.root || '/').replace(/\/?$/, '/');
  const route = (key, fallback) => AUMI.routes[key] || root + fallback;

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const storage = {
    get(key) {
      try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* modo privado */ }
    }
  };
  AUMI.utils = { $, $$, debounce, storage, emit };

  /* ------------------------------------------------------------------ money */
  AUMI.formatMoney = (cents) => {
    const value = Number.isFinite(Number(cents)) ? Math.round(Number(cents)) : 0;
    const negative = value < 0;
    const abs = Math.abs(value);
    const reais = Math.floor(abs / 100)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const centavos = String(abs % 100).padStart(2, '0');
    return `${negative ? '-' : ''}R$ ${reais},${centavos}`;
  };

  /* ------------------------------------------------------------------ toast */
  let toastTimer;
  AUMI.toast = (message, { duration = 3200 } = {}) => {
    let el = $('[data-toast]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.dataset.toast = '';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.hidden = false;
    // reinicia a animação de entrada
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, duration);
  };

  /* ---------------------------------------------------------------- drawers */
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';
  let activeDrawer = null;
  let lastTrigger = null;

  const getOverlay = () => {
    let overlay = $('[data-drawer-overlay]');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'overlay';
      overlay.dataset.drawerOverlay = '';
      overlay.dataset.closeDrawer = '';
      overlay.setAttribute('aria-hidden', 'true');
      document.body.appendChild(overlay);
    }
    return overlay;
  };

  const visibleFocusable = (el) =>
    $$(FOCUSABLE, el).filter((node) => node.offsetWidth || node.offsetHeight || node.getClientRects().length);

  const setClosedState = (drawer) => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');
  };

  AUMI.openDrawer = (name, trigger) => {
    const drawer = $(`[data-drawer="${name}"]`);
    if (!drawer) return false;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);
    lastTrigger = trigger || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    // força reflow para a transição rodar quando o drawer acaba de sair do inert
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    getOverlay().classList.add('is-open');
    document.documentElement.classList.add('no-scroll');
    document.body.classList.add('no-scroll');
    $$(`[data-open-drawer="${name}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    activeDrawer = drawer;
    const target = $('[data-drawer-focus]', drawer) || visibleFocusable(drawer)[0] || drawer;
    if (target === drawer && !drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
    setTimeout(() => target.focus({ preventScroll: true }), reducedMotion() ? 0 : 60);
    emit('aumi:drawer-open', { name });
    return true;
  };

  function closeDrawer(drawer, restoreFocus = true) {
    if (!drawer) return;
    const name = drawer.dataset.drawer;
    setClosedState(drawer);
    $$(`[data-open-drawer="${name}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    if (activeDrawer === drawer) activeDrawer = null;
    if (!activeDrawer) {
      getOverlay().classList.remove('is-open');
      document.documentElement.classList.remove('no-scroll');
      document.body.classList.remove('no-scroll');
      if (restoreFocus && lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
    }
    emit('aumi:drawer-close', { name });
  }

  AUMI.closeDrawers = () => {
    $$('[data-drawer].is-open').forEach((drawer) => closeDrawer(drawer));
    activeDrawer = null;
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-drawer]');
    if (opener && opener !== document.documentElement) {
      const name = opener.dataset.openDrawer;
      if (name === 'cart' && AUMI.settings.cartType === 'page') return; // segue o link para /cart
      if ($(`[data-drawer="${name}"]`)) {
        event.preventDefault();
        AUMI.openDrawer(name, opener);
      }
      return;
    }
    if (event.target.closest('[data-close-drawer]')) {
      event.preventDefault();
      AUMI.closeDrawers();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeDrawer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      AUMI.closeDrawers();
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = visibleFocusable(activeDrawer);
    if (!nodes.length) {
      event.preventDefault();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!activeDrawer.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ------------------------------------------------------------------- cart */
  const cart = (AUMI.cart = AUMI.cart || {});
  cart.sections = cart.sections || ['cart-drawer'];

  const sectionIds = () => Array.from(new Set(cart.sections)).slice(0, 5);

  const parseJSON = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Não foi possível atualizar a sacola.';
      const error = new Error(typeof message === 'string' ? message : 'Não foi possível atualizar a sacola.');
      error.status = response.status;
      throw error;
    }
    return data;
  };

  const renderSections = (sections) => {
    if (!sections) return;
    Object.values(sections).forEach((html) => {
      if (typeof html !== 'string') return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      $$('[data-cart-render]', doc).forEach((fresh) => {
        const key = fresh.dataset.cartRender;
        $$(`[data-cart-render="${key}"]`).forEach((current) => {
          const hadFocus = current.contains(document.activeElement) && document.activeElement.dataset
            ? document.activeElement.dataset.focusKey
            : null;
          current.innerHTML = fresh.innerHTML;
          Array.from(fresh.attributes).forEach((attr) => {
            if (attr.name.startsWith('data-state') || attr.name === 'style') current.setAttribute(attr.name, attr.value);
          });
          if (hadFocus) {
            const again = $(`[data-focus-key="${hadFocus}"]`, current);
            if (again) again.focus({ preventScroll: true });
          }
          AUMI.reveal(current);
        });
      });
    });
  };

  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count > 99 ? '99+' : String(count);
      el.dataset.count = String(count);
      el.hidden = count < 1;
    });
    $$('[data-cart-count-label]').forEach((el) => {
      el.textContent = count === 1 ? '1 item' : `${count} itens`;
    });
  };

  const fail = (error, source) => {
    AUMI.toast(error.message || 'Não foi possível atualizar a sacola.');
    emit('aumi:cart-error', { message: error.message, source });
    throw error;
  };

  const withSections = (payload) => {
    const ids = sectionIds();
    if (!ids.length) return payload;
    const url = window.location.pathname + window.location.search;
    if (payload instanceof FormData) {
      payload.set('sections', ids.join(','));
      payload.set('sections_url', url);
      return payload;
    }
    return { ...payload, sections: ids.join(','), sections_url: url };
  };

  const post = (url, payload) => {
    const isForm = payload instanceof FormData;
    return fetch(url, {
      method: 'POST',
      headers: isForm
        ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        : { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: isForm ? payload : JSON.stringify(payload)
    }).then(parseJSON);
  };

  const getCart = () =>
    fetch(`${route('cart', 'cart')}.js`, { headers: { Accept: 'application/json' } }).then(parseJSON);

  const finish = (cartData, source, extra = {}) => {
    cart.state = cartData;
    updateCount(cartData.item_count);
    emit('aumi:cart-updated', { cart: cartData, source, ...extra });
    return cartData;
  };

  let queue = Promise.resolve();
  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };

  cart.add = (input) =>
    enqueue(async () => {
      let payload;
      if (input instanceof HTMLFormElement) payload = new FormData(input);
      else if (input instanceof FormData) payload = input;
      else payload = { ...(input || {}) };
      if (!(payload instanceof FormData) && !payload.items && payload.id) {
        payload = { items: [{ id: payload.id, quantity: payload.quantity || 1, properties: payload.properties, selling_plan: payload.selling_plan }] };
      }
      try {
        const data = await post(`${route('cartAdd', 'cart/add')}.js`, withSections(payload));
        const sections = data.sections;
        const item = data.items ? data.items[0] : data;
        renderSections(sections);
        const cartData = await getCart();
        finish(cartData, 'add', { item });
        if (AUMI.settings.cartType !== 'page' && $('[data-drawer="cart"]')) {
          AUMI.openDrawer('cart');
        } else {
          AUMI.toast(`${(item && item.product_title) || 'Produto'} foi para a sacola.`);
        }
        return item;
      } catch (error) {
        return fail(error, 'add');
      }
    });

  cart.change = (key, quantity) =>
    enqueue(async () => {
      try {
        const data = await post(`${route('cartChange', 'cart/change')}.js`, withSections({ id: String(key), quantity: Math.max(0, Number(quantity) || 0) }));
        renderSections(data.sections);
        delete data.sections;
        return finish(data, 'change');
      } catch (error) {
        cart.refresh().catch(() => {});
        return fail(error, 'change');
      }
    });

  cart.update = (payload) =>
    enqueue(async () => {
      try {
        const data = await post(`${route('cartUpdate', 'cart/update')}.js`, payload);
        return finish(data, 'update');
      } catch (error) {
        return fail(error, 'update');
      }
    });

  cart.refresh = () =>
    enqueue(async () => {
      const ids = sectionIds();
      const [cartData, sections] = await Promise.all([
        getCart(),
        ids.length
          ? fetch(`${root}?sections=${encodeURIComponent(ids.join(','))}`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
          : Promise.resolve(null)
      ]);
      renderSections(sections);
      return finish(cartData, 'refresh');
    });

  // form[data-product-form]
  // Fase de captura: o preventDefault precisa vir antes do rastreio automático da Shopify,
  // senão o envio do form conta como um 2º "adicionou ao carrinho" (AddToCart duplicado no Pixel da Meta).
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = event.submitter || $('[type="submit"]', form);
    if (button) {
      if (button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    const errorBox = $('[data-product-form-error]', form);
    if (errorBox) errorBox.hidden = true;
    cart
      .add(new FormData(form))
      .catch((error) => {
        if (errorBox) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
        }
      })
      .finally(() => {
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      });
  }, true);

  // quantidade / remover / nota (delegação — sobrevive a re-render)
  document.addEventListener('click', (event) => {
    const changeBtn = event.target.closest('[data-cart-qty-change]');
    const removeBtn = event.target.closest('[data-cart-remove]');
    const btn = changeBtn || removeBtn;
    if (!btn) return;
    event.preventDefault();
    const line = btn.closest('[data-cart-line]');
    if (line) line.classList.add('is-updating');
    const quantity = removeBtn ? 0 : Number(btn.dataset.quantity);
    cart.change(btn.dataset.key, quantity).catch(() => line && line.classList.remove('is-updating'));
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[data-cart-qty-input]');
    if (!input) return;
    const line = input.closest('[data-cart-line]');
    if (line) line.classList.add('is-updating');
    cart.change(input.dataset.key, input.value).catch(() => line && line.classList.remove('is-updating'));
  });

  const saveNote = debounce((note) => {
    fetch(`${route('cartUpdate', 'cart/update')}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note })
    }).catch(() => {});
  }, 500);
  document.addEventListener('input', (event) => {
    const note = event.target.closest('textarea[data-cart-note]');
    if (note) saveNote(note.value);
  });

  /* CPF/CNPJ obrigatório antes do checkout (snippet cart-cpf) — vai para o pedido como atributo "CPF/CNPJ" */
  const CPF_KEY = 'CPF/CNPJ';
  const onlyDigits = (v) => String(v || '').replace(/\D/g, '');
  const validCpf = (d) => {
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
    for (let t = 9; t < 11; t++) {
      let sum = 0;
      for (let i = 0; i < t; i++) sum += Number(d[i]) * (t + 1 - i);
      if (((sum * 10) % 11) % 10 !== Number(d[t])) return false;
    }
    return true;
  };
  const validCnpj = (d) => {
    if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
    const calc = (len) => {
      const w = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
      const r = w.reduce((acc, n, i) => acc + Number(d[i]) * n, 0) % 11;
      return r < 2 ? 0 : 11 - r;
    };
    return calc(12) === Number(d[12]) && calc(13) === Number(d[13]);
  };
  const validTaxId = (v) => { const d = onlyDigits(v); return validCpf(d) || validCnpj(d); };
  const formatTaxId = (v) => {
    const d = onlyDigits(v).slice(0, 14);
    if (d.length <= 11) {
      return d.replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
    }
    return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})$/, '$1.$2.$3/$4-$5');
  };
  const cpfState = (input, showError) => {
    const box = input.closest('[data-cart-cpf]');
    const hint = box && $('[data-cart-cpf-hint]', box);
    const ok = validTaxId(input.value);
    input.setAttribute('aria-invalid', ok || !showError ? 'false' : 'true');
    if (hint) {
      hint.textContent = !ok && showError
        ? 'Confira o CPF ou CNPJ: o número não é válido.'
        : 'Obrigatório para emitir a nota fiscal e enviar o seu pedido.';
      hint.classList.toggle('cart-cpf__hint--error', !ok && showError);
    }
    return ok;
  };
  let savedTaxId = null;
  const saveTaxId = (value) => {
    if (value === savedTaxId) return Promise.resolve();
    return fetch(`${route('cartUpdate', 'cart/update')}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [CPF_KEY]: value } })
    }).then((r) => { if (r.ok) savedTaxId = value; });
  };
  document.addEventListener('input', (event) => {
    const input = event.target.closest('input[data-cart-cpf-input]');
    if (!input) return;
    const atEnd = input.selectionStart === input.value.length;
    input.value = formatTaxId(input.value);
    if (atEnd) input.setSelectionRange(input.value.length, input.value.length);
    $$('input[data-cart-cpf-input]').forEach((other) => { if (other !== input) other.value = input.value; });
    if (input.getAttribute('aria-invalid') === 'true') cpfState(input, true);
    if (validTaxId(input.value)) saveTaxId(input.value).catch(() => {});
  });
  document.addEventListener('focusout', (event) => {
    const input = event.target.closest('input[data-cart-cpf-input]');
    if (input && onlyDigits(input.value).length) cpfState(input, true);
  });
  // Bloqueia "Finalizar compra" sem CPF/CNPJ válido; com válido, grava o atributo e segue
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!btn || btn.dataset.cpfOk === '1') return;
    const scope = btn.closest('[data-drawer], .cart-summary') || document;
    const input = $('input[data-cart-cpf-input]', scope) || $('input[data-cart-cpf-input]');
    if (!input) return;
    event.preventDefault();
    event.stopPropagation();
    if (!cpfState(input, true)) {
      input.focus({ preventScroll: true });
      input.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      return;
    }
    btn.setAttribute('aria-busy', 'true');
    saveTaxId(input.value).catch(() => {}).finally(() => {
      btn.removeAttribute('aria-busy');
      btn.dataset.cpfOk = '1';
      btn.click();
      delete btn.dataset.cpfOk;
    });
  }, true);

  /* ----------------------------------------------------------------- reveal */
  let revealObserver = null;
  AUMI.reveal = (scope = document) => {
    const nodes = $$('[data-reveal]:not(.is-revealed)', scope instanceof Element || scope instanceof Document ? scope : document);
    if (scope instanceof Element && scope.matches('[data-reveal]:not(.is-revealed)')) nodes.push(scope);
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window) || reducedMotion() || !document.body.classList.contains('has-reveal')) {
      nodes.forEach((node) => node.classList.add('is-revealed'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    nodes.forEach((node) => revealObserver.observe(node));
  };

  /* ------------------------------------------------------------- slider-nav */
  class SliderNav extends HTMLElement {
    connectedCallback() {
      const selector = this.getAttribute('target');
      const section = this.closest('.shopify-section') || this.parentElement;
      this.slider = selector ? $(selector) : $('.slider', section);
      this.prev = $('[data-slider-prev]', this);
      this.next = $('[data-slider-next]', this);
      if (!this.slider) return;
      if (this.slider.id) {
        [this.prev, this.next].forEach((b) => b && b.setAttribute('aria-controls', this.slider.id));
      }
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);
      this.update = this.update.bind(this);
      this.prev && this.prev.addEventListener('click', this.onPrev);
      this.next && this.next.addEventListener('click', this.onNext);
      this.slider.addEventListener('scroll', this.update, { passive: true });
      this.ro = new ResizeObserver(this.update);
      this.ro.observe(this.slider);
      this.update();
    }
    disconnectedCallback() {
      if (!this.slider) return;
      this.slider.removeEventListener('scroll', this.update);
      this.ro && this.ro.disconnect();
    }
    step() {
      const first = this.slider.firstElementChild;
      if (!first) return this.slider.clientWidth;
      const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }
    go(direction) {
      this.slider.scrollBy({ left: direction * this.step(), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.slider.scrollWidth - this.slider.clientWidth - 2;
      const scrollable = max > 0;
      this.hidden = !scrollable && this.hasAttribute('auto-hide');
      if (this.prev) this.prev.disabled = this.slider.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.slider.scrollLeft >= max;
    }
  }
  if (!customElements.get('slider-nav')) customElements.define('slider-nav', SliderNav);

  /* ------------------------------------------------------ predictive-search */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      this.defaults = $('[data-predictive-default]', this);
      this.status = $('[data-predictive-status]', this);
      if (!this.input || !this.results) return;
      this.cache = new Map();
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');
      if (this.results.id) this.input.setAttribute('aria-controls', this.results.id);
      this.onInput = debounce(() => this.search(this.input.value.trim()), 260);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      $$('[data-search-term]', this).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.input.value = btn.dataset.searchTerm;
          this.input.focus();
          this.search(this.input.value);
        })
      );
      const clear = $('[data-search-clear]', this);
      if (clear) {
        clear.addEventListener('click', () => {
          this.input.value = '';
          this.input.focus();
          this.search('');
        });
      }
    }
    async search(term) {
      this.classList.toggle('has-term', term.length > 0);
      if (!term) {
        this.close();
        return;
      }
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setAttribute('loading', '');
      const params = new URLSearchParams({
        q: term,
        'resources[type]': this.dataset.resources || 'product,collection,query,page,article',
        'resources[limit]': this.dataset.limit || '6',
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search'
      });
      const base = AUMI.routes.predictiveSearch || `${root}search/suggest`;
      try {
        const response = await fetch(`${base}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = $('#predictive-search-results', doc);
        const html = fresh ? fresh.innerHTML : '';
        this.cache.set(term, html);
        this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        this.removeAttribute('loading');
      }
    }
    render(html) {
      this.results.innerHTML = html;
      this.results.hidden = false;
      if (this.defaults) this.defaults.hidden = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.selected = -1;
      const count = $$('[role="option"]', this.results).length;
      if (this.status) this.status.textContent = count ? `${count} sugestões disponíveis.` : 'Nenhum resultado.';
    }
    close() {
      this.results.hidden = true;
      this.results.innerHTML = '';
      if (this.defaults) this.defaults.hidden = false;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.selected = -1;
    }
    onKeydown(event) {
      const options = $$('[role="option"]', this.results);
      if (!options.length || this.results.hidden) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.selected = (this.selected + delta + options.length) % options.length;
        options.forEach((opt, i) => opt.setAttribute('aria-selected', i === this.selected ? 'true' : 'false'));
        const current = options[this.selected];
        this.input.setAttribute('aria-activedescendant', current.id);
        current.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.selected > -1) {
        const link = options[this.selected].matches('a') ? options[this.selected] : $('a', options[this.selected]);
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* =================================================================
     Chrome global (header, anúncio, popup, cookies, dock)
     ================================================================= */

  /* <announcement-bar> — rotação dos blocos */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-announcement-slide]', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = Math.max(3, Number(this.dataset.speed) || 5) * 1000;
      $('[data-announcement-prev]', this)?.addEventListener('click', () => this.show(this.index - 1, true));
      $('[data-announcement-next]', this)?.addEventListener('click', () => this.show(this.index + 1, true));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.start();
    }
    disconnectedCallback() { this.stop(); }
    show(i, manual) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, n) => {
        const active = n === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        $$('a', slide).forEach((a) => (active ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')));
      });
      if (manual) { this.stop(); this.start(); }
    }
    start() {
      if (reducedMotion() || this.timer || this.slides.length < 2) return;
      if (this.hasAttribute('data-inline') && window.matchMedia('(min-width: 750px)').matches) return;
      this.timer = setInterval(() => this.show(this.index + 1), this.interval);
    }
    stop() { clearInterval(this.timer); this.timer = null; }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* <sticky-header> — fundo ao rolar + --header-height */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.closest('.shopify-section') || this;
      this.onScroll = () => {
        const scrolled = window.scrollY > 8;
        if (scrolled !== this.scrolled) {
          this.scrolled = scrolled;
          this.classList.toggle('is-scrolled', scrolled);
          document.documentElement.classList.toggle('header-scrolled', scrolled);
        }
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.ro = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header-height', `${Math.round(this.getBoundingClientRect().height)}px`);
      });
      this.ro.observe(this);
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.ro && this.ro.disconnect();
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* <header-menu> — mega menu com <details>: hover no desktop, clique/teclado sempre */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.items = $$('details[data-menu-item]', this);
      const hover = window.matchMedia('(hover: hover)').matches;
      this.items.forEach((details) => {
        const summary = $('summary', details);
        details.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
          if (details.open) this.items.forEach((other) => other !== details && (other.open = false));
          this.classList.toggle('has-open', this.items.some((d) => d.open));
        });
        if (hover) {
          details.addEventListener('mouseenter', () => { clearTimeout(details._t); details.open = true; });
          details.addEventListener('mouseleave', () => { details._t = setTimeout(() => (details.open = false), 140); });
        }
        details.addEventListener('focusout', (e) => {
          if (!details.contains(e.relatedTarget)) details.open = false;
        });
      });
      this.onKey = (e) => {
        if (e.key !== 'Escape') return;
        const open = this.items.find((d) => d.open);
        if (open) { open.open = false; $('summary', open).focus(); }
      };
      this.onDocClick = (e) => { if (!this.contains(e.target)) this.items.forEach((d) => (d.open = false)); };
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocClick);
    }
    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDocClick);
    }
  }
  if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);

  /* <newsletter-popup> — atraso, supressão por X dias, cupom após inscrição */
  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.key = 'aumi:popup';
      this.dialog = $('[data-drawer]', this);
      if (!this.dialog) return;
      const formId = this.dataset.formId;
      // inscrição feita em qualquer formulário (newsletter-signup.js): não oferecer o popup de novo
      document.addEventListener('aumi:newsletter-success', () => {
        storage.set(this.key, { until: Date.now() + 365 * 864e5 });
        clearTimeout(this.timer);
      });
      // fallback do POST nativo: volta com #PopupNewsletter e reabre o popup com o cupom
      if (formId && window.location.hash === `#${formId}` && window.location.search.includes('customer_posted=true')) {
        storage.set(this.key, { until: Date.now() + 365 * 864e5 });
        requestAnimationFrame(() => AUMI.openDrawer(this.dialog.dataset.drawer));
        return;
      }
      if (window.Shopify?.designMode) return;
      const saved = storage.get(this.key);
      if (saved && saved.until > Date.now()) return;
      const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
      this.timer = setTimeout(() => {
        if (document.querySelector('[data-drawer].is-open')) return; // não interrompe outro painel
        // aparece uma única vez por navegador: marca ao abrir, feche ou não
        this.snooze();
        AUMI.openDrawer(this.dialog.dataset.drawer);
      }, delay);
      document.addEventListener('aumi:drawer-close', (e) => {
        if (e.detail.name === this.dialog.dataset.drawer) this.snooze();
      });
    }
    disconnectedCallback() { clearTimeout(this.timer); }
    snooze() {
      storage.set(this.key, { until: Date.now() + 3650 * 864e5 });
    }
  }
  if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);

  /* Mensagens de sucesso de formulários 'customer': mostra só no formulário que foi enviado */
  const syncPostedMessages = () => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    $$('[data-posted-for]').forEach((el) => {
      if (el.dataset.postedFor !== hash) el.hidden = true;
    });
  };

  /* Copiar cupom: [data-copy="TEXTO"] */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-copy]');
    if (!btn) return;
    event.preventDefault();
    const text = btn.dataset.copy;
    const done = () => AUMI.toast(`Cupom ${text} copiado.`);
    if (navigator.clipboard) navigator.clipboard.writeText(text).then(done).catch(done);
    else done();
  });

  /* <cookie-banner> — LGPD + Shopify Customer Privacy API */
  class CookieBanner extends HTMLElement {
    connectedCallback() {
      this.key = 'aumi:consent';
      if (window.Shopify?.designMode && this.hasAttribute('data-preview')) {
        this.hidden = false;
        return;
      }
      $('[data-consent-accept]', this)?.addEventListener('click', () => this.decide(true));
      $('[data-consent-essential]', this)?.addEventListener('click', () => this.decide(false));
      this.loadApi().then((api) => {
        const saved = storage.get(this.key);
        if (saved) return;
        if (api && this.dataset.respectRegion === 'true' && typeof api.shouldShowBanner === 'function' && !api.shouldShowBanner()) return;
        this.show();
      });
    }
    loadApi() {
      return new Promise((resolve) => {
        const ready = () => resolve(window.Shopify && window.Shopify.customerPrivacy ? window.Shopify.customerPrivacy : null);
        if (window.Shopify && window.Shopify.customerPrivacy) return ready();
        if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => (error ? resolve(null) : ready()));
          setTimeout(ready, 2500);
        } else {
          resolve(null);
        }
      });
    }
    show() {
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-visible'));
    }
    decide(accepted) {
      storage.set(this.key, { accepted, at: Date.now() });
      const api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.setTrackingConsent === 'function') {
        const consent = { analytics: accepted, marketing: accepted, preferences: accepted, sale_of_data: accepted };
        try {
          api.setTrackingConsent(consent, () => {});
        } catch (e) {
          try { api.setTrackingConsent(accepted, () => {}); } catch (err) { /* API indisponível */ }
        }
      }
      this.classList.remove('is-visible');
      setTimeout(() => { this.hidden = true; }, reducedMotion() ? 0 : 300);
      emit('aumi:consent', { accepted });
    }
  }
  if (!customElements.get('cookie-banner')) customElements.define('cookie-banner', CookieBanner);

  /* Mobile dock: marca o item ativo ao abrir busca/sacola */
  // data-active-drawer (e não data-open-drawer): senão o <html> casa com closest('[data-open-drawer]') e todo clique reabre o painel
  document.addEventListener('aumi:drawer-open', (e) => document.documentElement.setAttribute('data-active-drawer', e.detail.name));
  document.addEventListener('aumi:drawer-close', () => {
    if (!document.querySelector('[data-drawer].is-open')) document.documentElement.removeAttribute('data-active-drawer');
  });

  /* Acordeões do rodapé: abertos no desktop, recolhidos no celular */
  const footerAccordions = (scope = document) => {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    $$('details[data-footer-accordion]', scope).forEach((details) => {
      details.open = !mobile;
    });
  };
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('details[data-footer-accordion] > summary');
    if (summary && window.matchMedia('(min-width: 750px)').matches) event.preventDefault();
  });

  /* ----------------------------------------------------------------- portal */
  // Drawers com [data-portal] saem do header (que é sticky e cria stacking context) e vão para o <body>.
  const portal = (scope = document) => {
    $$('[data-drawer][data-portal]', scope).forEach((drawer) => {
      if (drawer.parentElement === document.body) return;
      $$(`body > [data-drawer="${drawer.dataset.drawer}"][data-portal]`).forEach((old) => {
        if (old !== drawer) old.remove();
      });
      document.body.appendChild(drawer);
    });
  };

  /* ------------------------------------------------------------------- init */
  const init = () => {
    portal(document);
    footerAccordions(document);
    // Drawers começam fechados e fora da ordem de foco
    $$('[data-drawer]').forEach((drawer) => {
      if (!drawer.classList.contains('is-open')) setClosedState(drawer);
    });
    // Evita drawer de carrinho duplicado (snippet no layout + seção no grupo)
    $$('[data-drawer="cart"]').slice(1).forEach((dup) => dup.remove());
    getOverlay();
    AUMI.reveal(document);
    syncPostedMessages();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Editor de temas: re-inicializa ao carregar/selecionar seções
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-drawer]', e.target).forEach((drawer) => setClosedState(drawer));
    portal(e.target);
    footerAccordions(e.target);
    AUMI.reveal(e.target);
  });
  document.addEventListener('shopify:section:unload', () => AUMI.closeDrawers());
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = $('[data-drawer]', e.target);
    if (drawer && drawer.dataset.editorOpen !== undefined) AUMI.openDrawer(drawer.dataset.drawer);
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const megaDetails = e.target.closest('details[data-menu-item]');
    if (megaDetails) megaDetails.open = false;
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = $('[data-drawer]', e.target);
    if (drawer && drawer.dataset.editorOpen !== undefined) AUMI.closeDrawers();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const megaDetails = e.target.closest('details[data-menu-item]');
    if (megaDetails) megaDetails.open = true;
    const bar = e.target.closest('announcement-bar');
    if (bar && bar.show) {
      const i = bar.slides ? bar.slides.indexOf(e.target) : -1;
      if (i > -1) { bar.stop(); bar.show(i); }
    }
  });
})();
